import React from 'react';
import { styled } from '@compiled/react';
import { graphql, useFragment, useMutation } from 'react-relay';
import { token } from '@atlaskit/tokens';
import { useFlagsService } from '@atlassian/jira-flags';
import { useIntl } from '@atlassian/jira-intl';
import { fireTrackAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import type { versionDates_softwareReleasesLaunchCard$key } from '@atlassian/jira-relay/src/__generated__/versionDates_softwareReleasesLaunchCard.graphql';
import type {
	versionDates_updateVersionReleaseDateMutation as UpdateVersionReleaseDateMutation,
	versionDates_updateVersionReleaseDateMutation$data as UpdateVersionReleaseDateMutation$data,
} from '@atlassian/jira-relay/src/__generated__/versionDates_updateVersionReleaseDateMutation.graphql';
import type {
	versionDates_updateVersionStartDateMutation as UpdateVersionStartDateMutation,
	versionDates_updateVersionStartDateMutation$data as UpdateVersionStartDateMutation$data,
} from '@atlassian/jira-relay/src/__generated__/versionDates_updateVersionStartDateMutation.graphql';
import { VERSION_DATE_FORMAT_OPTION as DATE_FORMAT_OPTION } from '@atlassian/jira-software-releases-version-date-utils/src/common/constants.tsx';
import { fireFieldUpdateErrorAnalytics } from '../../common/utils/field-update-error-analytics/index.tsx';
import { AsyncEditableDatePicker as EditableDatePicker } from './editable-date-picker/async.tsx';
import { convertDatetimeToDate } from './editable-date-picker/utils.tsx';
import messages from './messages.tsx';

const BAD_START_RELEASE_DATE_ORDER_ERROR = 'BAD_START_RELEASE_DATE_ORDER';

type FormattedDates = {
	formattedStartDate: string;
	formattedReleaseDate: string;
};

const useFormattedDates = (
	startDate: string | null,
	releaseDate: string | null,
): FormattedDates => {
	const { formatMessage, formatDate } = useIntl();

	return {
		formattedStartDate:
			startDate === null
				? formatMessage(messages.startDateNotSet)
				: formatDate(new Date(startDate), {
						timeZone: DATE_FORMAT_OPTION.timeZone,
						dateStyle: 'long',
					}),
		formattedReleaseDate:
			releaseDate === null
				? formatMessage(messages.releaseDateNotSet)
				: formatDate(new Date(releaseDate), {
						timeZone: DATE_FORMAT_OPTION.timeZone,
						dateStyle: 'long',
					}),
	};
};

export const VersionDates = ({
	jiraVersion,
}: {
	jiraVersion: versionDates_softwareReleasesLaunchCard$key;
}) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { showFlag } = useFlagsService();
	const { formatMessage } = useIntl();

	const version = useFragment(
		graphql`
			fragment versionDates_softwareReleasesLaunchCard on JiraVersion {
				id
				canEdit
				startDate
				releaseDate
			}
		`,
		jiraVersion,
	);

	const [updateVersionStartDate] = useMutation<UpdateVersionStartDateMutation>(graphql`
		mutation versionDates_updateVersionStartDateMutation($input: JiraUpdateVersionStartDateInput!) {
			jira {
				updateVersionStartDate(input: $input) {
					success
					errors {
						extensions {
							statusCode
							errorType
						}
					}
					version {
						startDate
					}
				}
			}
		}
	`);

	const [updateVersionReleaseDate] = useMutation<UpdateVersionReleaseDateMutation>(graphql`
		mutation versionDates_updateVersionReleaseDateMutation(
			$input: JiraUpdateVersionReleaseDateInput!
		) {
			jira {
				updateVersionReleaseDate(input: $input) {
					success
					version {
						releaseDate
					}
				}
			}
		}
	`);

	const handleStartDateMutationError = (data: UpdateVersionStartDateMutation$data): void => {
		const flagText = {
			title: messages.startDateGenericErrorFlagTitle,
			description: messages.genericErrorFlagDescription,
		};

		if (data.jira?.updateVersionStartDate?.errors) {
			const { extensions } = data.jira.updateVersionStartDate.errors[0];
			if (extensions?.errorType === BAD_START_RELEASE_DATE_ORDER_ERROR) {
				flagText.title = messages.badReleaseStartDateOrderErrorFlagTitle;
				flagText.description = messages.badReleaseStartDateOrderErrorFlagDescription;
			}
		}
		showFlag({
			type: 'warning',
			...flagText,
			messageId:
				'software-releases-launch-card.ui.version-dates.show-flag.warning.handle-start-date-mutation-error.show-flag.warning',
			messageType: 'transactional',
		});
	};

	const handleStartDateChange = (startDate: string | null): void => {
		updateVersionStartDate({
			variables: {
				input: {
					id: version.id,
					startDate,
				},
			},
			onCompleted: (data: UpdateVersionStartDateMutation$data) => {
				if (!data.jira?.updateVersionStartDate?.success) {
					handleStartDateMutationError(data);
				} else {
					fireTrackAnalytics(
						createAnalyticsEvent({
							action: 'updated',
							actionSubject: 'versionStartDate',
						}),
						'versionStartDate updated',
					);
				}
			},
			onError: () => {
				fireFieldUpdateErrorAnalytics();
				showFlag({
					type: 'error',
					title: messages.startDateGenericErrorFlagTitle,
					description: messages.genericErrorFlagDescription,
					messageId:
						'software-releases-launch-card.ui.version-dates.show-flag.error.on-error.show-flag.error',
					messageType: 'transactional',
				});
			},
			optimisticResponse: {
				jira: {
					updateVersionStartDate: {
						success: true,
						errors: [],
						version: {
							id: version.id,
							startDate,
						},
					},
				},
			},
		});
	};

	const handleReleaseDateChange = (releaseDate: string | null): void => {
		updateVersionReleaseDate({
			variables: {
				input: {
					id: version.id,
					releaseDate,
				},
			},
			onCompleted: (data: UpdateVersionReleaseDateMutation$data) => {
				if (!data.jira?.updateVersionReleaseDate?.success) {
					showFlag({
						type: 'warning',
						title: messages.releaseDateErrorFlagTitle,
						description: messages.genericErrorFlagDescription,
						messageId:
							'software-releases-launch-card.ui.version-dates.show-flag.warning.on-completed.show-flag.warning',
						messageType: 'transactional',
					});
				} else {
					fireTrackAnalytics(
						createAnalyticsEvent({
							action: 'updated',
							actionSubject: 'versionReleaseDate',
						}),
						'versionReleaseDate updated',
					);
				}
			},
			onError: () => {
				fireFieldUpdateErrorAnalytics();
				showFlag({
					type: 'error',
					title: messages.releaseDateErrorFlagTitle,
					description: messages.genericErrorFlagDescription,
					messageId:
						'software-releases-launch-card.ui.version-dates.show-flag.error.on-error.show-flag.error.1',
					messageType: 'transactional',
				});
			},
			optimisticResponse: {
				jira: {
					updateVersionReleaseDate: {
						success: true,
						version: {
							id: version.id,
							releaseDate,
						},
					},
				},
			},
		});
	};

	const { formattedStartDate, formattedReleaseDate } = useFormattedDates(
		// @ts-expect-error - Argument of type 'string | null | undefined' is not assignable to parameter of type 'string | null'.
		version.startDate,
		version.releaseDate,
	);

	return (
		<DateContainer>
			<EditableDatePicker
				analyticsEventName="versionStartDate"
				// @ts-expect-error - Type 'boolean | null | undefined' is not assignable to type 'boolean | null'.
				canEdit={version.canEdit}
				// @ts-expect-error - Type 'string | null | undefined' is not assignable to type 'string | null'.
				currentDate={version.startDate}
				displayDate={formattedStartDate}
				label={formatMessage(messages.startDateFieldLabel)}
				maxDate={convertDatetimeToDate(version.releaseDate)}
				onUpdateDate={handleStartDateChange}
				testId="software-releases-launch-card.ui.version-dates.start-date-editable-date-picker"
			/>
			<EditableDatePicker
				analyticsEventName="versionReleaseDate"
				// @ts-expect-error - Type 'boolean | null | undefined' is not assignable to type 'boolean | null'.
				canEdit={version.canEdit}
				// @ts-expect-error - Type 'string | null | undefined' is not assignable to type 'string | null'.
				currentDate={version.releaseDate}
				displayDate={formattedReleaseDate}
				label={formatMessage(messages.releaseDateFieldLabel)}
				minDate={convertDatetimeToDate(version.startDate)}
				onUpdateDate={handleReleaseDateChange}
				testId="software-releases-launch-card.ui.version-dates.release-date-editable-date-picker"
			/>
		</DateContainer>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const DateContainer = styled.div({
	display: 'flex',
	gap: token('space.100'),
	justifyContent: 'space-between',
});
